<template>
  <div
    class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]"
  >
    <div class="w-full">
      <div v-if="isLoading" class="text-gray-600 font-sans py-4">
        <p>Loading data...</p>
      </div>
      <div
        v-else-if="!authStore.user?.organization_id"
        class="p-4 bg-yellow-50 font-sans border border-yellow-200 rounded-md mb-4"
      >
        <p class="text-amber-700">Unable to load organization data. Please try refreshing the page.</p>
        <button
          @click="authStore.fetchUserData"
          class="bg-amber-600 font-sans text-white px-4 py-1 rounded-md mt-2"
        >
          Retry Loading Organization Data
        </button>
      </div>
      <div v-else>
        <QuestionField
          v-model="answerState.userAnswer"
          :label="questionLabel"
          :question="questionText"
          placeholder="Enter the population or community your organization serves..."
        />
        <p v-if="errorMessage" class="text-red-500 font-sans">
          {{ errorMessage }}
        </p>
        <p v-if="v$.userAnswer.$error" class="text-red-500 font-sans">
          {{ v$.userAnswer.$errors[0].$message }}
        </p>
        <div class="flex items-center mt-2">
          <span v-if="isSaving" class="font-minion text-gray-600"
            >Saving...</span
          >
          <span v-if="saveSuccessful" class="font-minion text-green-600"
            >Saved successfully</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed, watch } from "vue";
import QuestionField from "@/components/QuestionField.vue";
import { useAuthStore } from "@/stores/auth";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import global from "@/global";
import { useAutoSave } from "@/composables/useAutoSave";
import { useStepValidation } from "@/composables/useStepValidation";

const isLoading = ref(true);
const questionLabel = ref("");
const questionText = ref("");
const errorMessage = ref("");
const answerState = reactive({ userAnswer: "" });
const wordCount = computed(
  () => answerState.userAnswer.split(/\s+/).filter((word) => word !== "").length
);

// Validation rules
const rules = {
  userAnswer: {
    required: helpers.withMessage("Answer is required!", required),
  },
};

const v$ = useVuelidate(rules, answerState, {
  $lazy: true // This makes validation lazy - only validates on submit
});

// Fetching the auth store
const authStore = useAuthStore();

// Using the auto-save composable
const { isSaving, saveSuccessful, lastSavedValue, setupAutoSave } = useAutoSave(
  () => answerState.userAnswer,
  saveUserAnswer,
  () => true, // Remove validation check from auto-save
  1000
);

// Define categoryId and questionId as refs
const categoryId = ref("");
const questionId = ref("");

// Fetching question data directly using fetch API
async function fetchQuestionData() {
  try {
    const res = await fetch(
      `${global.API_URL}/questions/community_impact_impact`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      }
    );

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const data = await res.json();
    questionLabel.value = data.question.label;
    questionText.value = data.question.title;
    // Store category_id and question_id for later use
    categoryId.value = data.question.category_id;
    questionId.value = data.question.id;
  } catch (error) {
    errorMessage.value = "Failed to load question data. Please try again.";
    console.error(error);
  }
}

// Fetching user answer directly using fetch API
async function getUserAnswer() {
  // Get organization ID from auth store
  const organizationId = authStore.user.organization_id;

  // Check if organizationId and questionId are valid
  if (!organizationId) {
    console.error("Invalid organizationId:", organizationId);
    errorMessage.value = "Invalid organization ID. Please try refreshing the page.";
    return;
  }

  if (!questionId.value) {
    console.error("Invalid questionId:", questionId.value);
    errorMessage.value =
      "Question ID not available. Please try refreshing the page.";
    return;
  }

  try {
    const res = await fetch(
      `${global.API_URL}/answers?question_id=${questionId.value}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      }
    );

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const data = await res.json();
    let answer = data.answer;
    if (answer != null) {
      answerState.userAnswer = answer.answer;
      lastSavedValue.value = answer.answer; // Store last saved answer
    }
  } catch (error) {
    console.error("Failed to fetch user answer:", error);
    errorMessage.value =
      "Failed to load your previous answer. You can still create a new one.";
  }
}

// Update saveUserAnswer to not validate immediately
async function saveUserAnswer() {
  // Don't save if the answer hasn't changed
  if (answerState.userAnswer === lastSavedValue.value) {
    return;
  }

  // Get organization ID from auth store
  const organizationId = authStore.user.organization_id;

  // Double check that we have a valid organization ID
  if (!organizationId) {
    errorMessage.value = "Invalid organization ID. Please try refreshing the page.";
    return;
  }

  try {
    const res = await fetch(`${global.API_URL}/answers`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        category_id: categoryId.value,
        question_id: questionId.value,
        answer: answerState.userAnswer,
      }),
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const data = await res.json();
    if (data.status) {
      errorMessage.value = "";
      lastSavedValue.value = answerState.userAnswer;
    } else {
      throw new Error("Failed to save answer");
    }
  } catch (error) {
    errorMessage.value = "Failed to save your answer. Please try again.";
    console.error("Failed to save user answer:", error);
  }
}

// Update validateStep to match the new validation pattern
const validateStep = async () => {
  // Trigger Vuelidate validation
  const result = await v$.value.$validate();
  
  // If there are validation errors, immediately return false
  if (!result) {
    return false;
  }

  // If we have unsaved changes, save them first
  if (answerState.userAnswer !== lastSavedValue.value) {
    try {
      await saveUserAnswer();
      return true;
    } catch (error) {
      console.error("Failed to save answer:", error);
      errorMessage.value = "Please ensure your answer is saved before proceeding.";
      return false;
    }
  }

  return true;
};

// Use the step validation composable
useStepValidation({
  initialValidState: false, // Start with invalid state
  validateFn: validateStep,
});

onMounted(async () => {
  try {
    isLoading.value = true;
    errorMessage.value = "";

    // First ensure we have the latest user data with organization ID
    await authStore.fetchUserData();
    console.log("Organization ID after fetch:", authStore.user?.organization_id);

    if (!authStore.user?.organization_id) {
      errorMessage.value = "Unable to load organization data. Please try refreshing the page.";
      console.error("No organization ID available");
      return;
    }

    // Then load the question data
    await fetchQuestionData();
    console.log("Question ID after fetch:", questionId.value);

    if (!questionId.value) {
      errorMessage.value = "Unable to load question data. Please try refreshing the page.";
      console.error("No question ID available");
      return;
    }

    // Set up auto-save watcher
    setupAutoSave(() => answerState.userAnswer);

    // Finally fetch the user's answer
    await getUserAnswer();
    console.log("Answer loaded:", answerState.userAnswer);

  } catch (error) {
    console.error("Error during component initialization:", error);
    errorMessage.value = "An error occurred while loading the data. Please try refreshing the page.";
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped>

</style> 